import { makeDsn } from '@sentry/core'

export type SentryIngestProbeResult = {
  ok: boolean
  status: number | null
  swControlled: boolean
  crossOriginIsolated: boolean
  ingestUrl: string | null
  detail: string
}

function buildProbeEnvelopeUrl(dsn: string): string | null {
  const parsed = makeDsn(dsn)
  if (!parsed?.host || !parsed.projectId || !parsed.publicKey) return null
  const params = new URLSearchParams({
    sentry_version: '7',
    sentry_key: parsed.publicKey,
    sentry_client: 'gh-webapp-ingest-probe/1.0',
  })
  return `https://${parsed.host}/api/${parsed.projectId}/envelope/?${params}`
}

/**
 * Direct POST to Sentry ingest (same URL shape as the SDK). Used to distinguish
 * service-worker proxy issues from tracking protection / ad blockers / network blocks.
 */
export async function probeSentryIngestFromPage(): Promise<SentryIngestProbeResult> {
  const swControlled = typeof navigator !== 'undefined' && Boolean(navigator.serviceWorker?.controller)
  const crossOriginIsolated =
    typeof window !== 'undefined' && Boolean(window.crossOriginIsolated)

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    return {
      ok: false,
      status: null,
      swControlled,
      crossOriginIsolated,
      ingestUrl: null,
      detail: 'VITE_SENTRY_DSN is not set for this build.',
    }
  }

  const ingestUrl = buildProbeEnvelopeUrl(dsn)
  if (!ingestUrl) {
    return {
      ok: false,
      status: null,
      swControlled,
      crossOriginIsolated,
      ingestUrl: null,
      detail: 'Could not parse VITE_SENTRY_DSN.',
    }
  }

  try {
    const response = await fetch(ingestUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-sentry-envelope' },
      body: '{}',
      referrerPolicy: 'strict-origin',
    })
    if (response.ok) {
      return {
        ok: true,
        status: response.status,
        swControlled,
        crossOriginIsolated,
        ingestUrl,
        detail:
          'Direct ingest fetch succeeded. Console CORS errors are unlikely to be caused by SW proxying.',
      }
    }
    return {
      ok: false,
      status: response.status,
      swControlled,
      crossOriginIsolated,
      ingestUrl,
      detail: `Sentry ingest returned HTTP ${response.status}.`,
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    const blockerHint =
      'Firefox often reports tracking protection or ad blockers as “CORS request did not succeed” with status (null). The app SW bypasses *.sentry.io (no respondWith).'
    return {
      ok: false,
      status: null,
      swControlled,
      crossOriginIsolated,
      ingestUrl,
      detail: `${message}. ${blockerHint}`,
    }
  }
}
